import classNames from "classnames";
import React, { useState } from "react";
import { Auth } from "shared/@auth/Auth";
import Button, { ButtonPurpose, ButtonSize } from "shared/Button";
import Loader from "shared/Loader";
import TabActionBar from "shared/TabActionBar";
import Table from "shared/Table";
import ContactEdit from "./ContactEdit";
import styles from "./index.module.css";
import { useContacts } from "./useContacts";

import { Form } from "shared/@forms/Form";
import { FormActionBar } from "shared/@forms/FormActionBar";

export default function Contacts({ iboNumber, refetchEmails }) {
  const { hasPermission } = Auth.useContainer();
  const { contacts, update, removeCoIBO, downloadBadge, isWaiting } =
    useContacts(iboNumber);

  const [editData, setEditData] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const canUpdate = hasPermission("membership.ibo.update");

  return (
    <div className={styles.container}>
      {contacts == null || isWaiting ? (
        <Loader />
      ) : (
        <>
          {canUpdate && contacts.length < 2 && (
            <TabActionBar>
              <Button
                onClick={() => {
                  setEditData(createNewContactData());
                }}
                disabled={editData != null}
              >
                Add IBO-B
              </Button>
            </TabActionBar>
          )}

          <Table>
            <thead>
              <tr>
                {canUpdate ? <th>Edit</th> : <th>Badge</th>}

                <th>Type</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                {canUpdate && <th style={{ textAlign: "right" }}>Remove</th>}
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => {
                const isBeingEdited =
                  editData && editData.contactPK === c.contactPK;

                // Don't show IBO-B at all if it's currently being deleted.
                if (c.type === "IBO-B" && isDeleting) {
                  return null;
                }

                return (
                  <tr
                    key={c.contactPK}
                    className={classNames({
                      [styles.editing]: isBeingEdited,
                    })}
                  >
                    <td>
                      {canUpdate && (
                        <Button
                          size="small"
                          disabled={isBeingEdited}
                          className={styles.contactButton}
                          onClick={() => {
                            setEditData(c);
                          }}
                        >
                          Edit
                        </Button>
                      )}

                      <Button
                        size="small"
                        disabled={isBeingEdited}
                        className={styles.contactButton}
                        onClick={() => {
                          downloadBadge(iboNumber, c.contactPK);
                        }}
                      >
                        Badge
                      </Button>
                    </td>

                    <td>{c.type}</td>
                    <td>
                      {c.lastName}, {c.firstName}
                      <div>
                        {c.avatarUrl ? (
                          <img
                            alt={c.type}
                            className={styles.profilePhoto}
                            src={c.avatarUrl}
                          />
                        ) : (
                          ""
                        )}
                      </div>
                    </td>
                    <td>{c.phone}</td>
                    <td>{c.email}</td>

                    {canUpdate && (
                      <td align="right">
                        {c.type === "IBO-A" ? (
                          <span className={styles.textFaded}>
                            Cannot Remove
                          </span>
                        ) : (
                          <Button
                            disabled={isConfirmingDelete}
                            purpose={ButtonPurpose.delete}
                            size={ButtonSize.small}
                            onClick={() => {
                              setIsConfirmingDelete(true);
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {isConfirmingDelete && (
            <Form
              className={styles.confirmDeleteForm}
              onSubmit={async () => {
                setIsDeleting(true);

                return removeCoIBO().finally(() => {
                  setIsDeleting(false);
                  setIsConfirmingDelete(false);
                  console.log("called");
                });
              }}
            >
              <header>
                <h2>Confirm</h2>
              </header>
              <p>Are you sure you want to remove IBO-B?</p>
              <FormActionBar>
                <Button
                  disabled={isDeleting}
                  onClick={() => {
                    setIsConfirmingDelete(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isDeleting}
                  purpose={ButtonPurpose.delete}
                >
                  Remove IBO-B
                </Button>
              </FormActionBar>
            </Form>
          )}

          {editData && (
            <ContactEdit
              data={editData}
              disabled={isUpdating}
              onSubmit={async (contact) => {
                setIsUpdating(true);
                try {
                  await update(contact);
                  setEditData(null);
                  await refetchEmails();
                } catch (err) {
                  console.error(err);
                } finally {
                  setIsUpdating(false);
                }
              }}
              onCancel={() => {
                setEditData(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Creates an empty IBO-B object.
 */
function createNewContactData() {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    type: "IBO-B",
  };
}
